import {
  lastSeenBehaviour,
  markBehaviourSeen,
  noteViewer,
  resolveSystemNote,
} from '@shared/ai-data-policy/notes';
import { useAiDataPolicyStore } from '@shared/ai-data-policy/store';
import { behaviourOf } from '@shared/ai-data-policy/types';

import { useConversationsStore } from '../store/conversations.store';
import type { ChatSystemNote, Conversation } from '../types';

let noteSeq = 0;

export function conversationHasInvestorData(conversation: Conversation | null): boolean {
  if (!conversation) return false;
  return (
    (conversation.investorData ?? Boolean(conversation.sessionId)) ||
    conversation.messages.some(
      (m) => m.role === 'assistant' && (m.route?.categories.length ?? 0) > 0,
    )
  );
}

export function appendDueSystemNote(
  conversationId: string,
  selectedModelLabel: string | null = null,
): void {
  const convStore = useConversationsStore.getState();
  const conversation = convStore.conversations[conversationId];
  if (!conversation || conversation.messages.length === 0) return;
  if (conversation.messages.some((m) => m.status === 'streaming')) return;

  const { policy, remoteChange } = useAiDataPolicyStore.getState();
  if (!policy) return;

  const viewer = noteViewer();
  const settled = conversation.messages.filter((m) => m.status !== 'queued');
  const anchor = settled.at(-1);
  const lastAssistant = [...settled].reverse().find((m) => m.role === 'assistant');
  const involved =
    lastAssistant?.status === 'complete' && (lastAssistant.route?.categories.length ?? 0) > 0;

  const spec = resolveSystemNote({
    policy,
    lastSeen: lastSeenBehaviour(viewer),
    transition: remoteChange,
    involvedInternalData: involved,
  });
  if (!spec) return;

  const note: ChatSystemNote = {
    id: `note-${String(Date.now())}-${String(++noteSeq)}`,
    afterMessageId: anchor?.id ?? null,
    kind: spec.kind,
    actorName: spec.actorName,
    at: spec.at,
    modelLabel: selectedModelLabel ?? lastAssistant?.route?.selectedModel.label ?? null,
    createdAt: Date.now(),
  };
  convStore.appendSystemNote(conversationId, note);
  markBehaviourSeen(viewer, spec.behaviour);
}

export function markCurrentPolicyNoteSeen(): void {
  const { policy } = useAiDataPolicyStore.getState();
  if (policy) markBehaviourSeen(noteViewer(), behaviourOf(policy.value));
}
